<script setup lang="ts">

import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {useSystemStore} from "@/stores/system.store";
import {onMounted} from "vue";
import {useRouter} from "vue-router";
import {useExtensionStore} from "@/stores/extension.store";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {useCreditStore} from "@/stores/credit.store";
import retryWhileUnreachable from "@/core/http/retryWhileUnreachable";

const systemStore = useSystemStore();
const extensionStore = useExtensionStore();
const subscriptionStore = useSubscriptionPlanStore();
const creditStore = useCreditStore();
const router = useRouter();

/**
 * On mounted update the host name and hydrate the stores.
 * Finally, redirect to the home page.
 */
onMounted(async () => {
    return hydrateStores()
        .then(() => {
            startDetection();

            router.replace({name: 'home'});
        })
        .catch(() => {
            router.replace({name: 'login', query: {failed: 1}});
        });
});

/**
 * Notify the main process that the user is authenticated, which starts the detection process.
 */
const startDetection = (): void => {
    window.ipcRenderer.authenticated();
};

/**
 * Hydrate the core application stores.
 *
 * Each store is retried on its own while the API is unreachable, so that a
 * request failing because the network is not up yet no longer sends the
 * pharmacy back to the login screen — and a store already hydrated is not
 * loaded twice.
 */
const hydrateStores = () => {
    return Promise.all([
        () => systemStore.init(),
        () => extensionStore.loadExtensions(),
        () => subscriptionStore.loadSubscriptionPlans(),
        () => creditStore.refresh()
    ].map(hydrate => retryWhileUnreachable(async () => hydrate())));
};
</script>

<template>
  <div class="h-screen overflow-hidden bg-primary-lightest flex flex-col">
    <BaseFlexSpinner />
  </div>
</template>
